import './CommentGroup.css'
import React, { useEffect, useState, useRef } from 'react'
import { useEvent } from 'react-use'
import classnames from 'classnames'
import formatMessage from 'format-message'
import uuid from 'uuid'
import Comment from './Comment'
import { Spring } from 'react-spring/renderprops'
import {
  isCommentGroupStacked,
  byCreatedAt,
  generatePlaceholder,
  areEqual
} from './CommentGroup.utils.js'
import {
  UNSTACKED_COMMENT_GROUP,
  STACKED_COMMENT_GROUP,
  MINIMAL_PREDICTED_COMMENT_WITH_REPLIES_HEIGHT
} from '../constants'

// If one attempts to hookify the following, ensure that
//   DIG-1743 (stacking/unstacking flickering) does not reoccur

class CommentGroup_ extends React.Component {
  componentDidMount() {
    this.props.setClientHeight(this.props.div.current.clientHeight)
    setTimeout(() => this.props.updateClientHeight(), 25)

    // when the comment groups intially render we have to decide who needs to stack
    // and who can be unstacked. This causes the managedTop prop to be unreliable upon
    // mounting because it can change as comment groups change their stacking - so we
    // prevent any repositioning for a bit to avoid "jumping."
    const isNew = this.props.commentReplies.length === 0
    if (isNew === false) {
      setTimeout(() => {
        this.props.setPreventCommentGroupTopAnimation(false)
      }, 300)
    } else {
      this.props.setPreventCommentGroupTopAnimation(false)
    }

    if (window.ResizeObserver) {
      this.props.resizeObserver.current = new window.ResizeObserver(entries => {
        window.requestAnimationFrame(() => {
          const isStacked = isCommentGroupStacked(
            this.props.isSelected,
            this.props.shouldCommentGroupStack
          )

          for (let entry of entries) {
            if (this.props.height.current !== entry.contentRect.height) {
              let newHeight = entry.contentRect.height

              const commentRepliesHaveNotRendered =
                this.props.commentReplies.length > 1 &&
                newHeight < MINIMAL_PREDICTED_COMMENT_WITH_REPLIES_HEIGHT

              if (commentRepliesHaveNotRendered) {
                newHeight = MINIMAL_PREDICTED_COMMENT_WITH_REPLIES_HEIGHT
              }

              this.props.onCommentGroupHeightChange(
                this.props.annotation.id,
                newHeight,
                isStacked ? STACKED_COMMENT_GROUP : UNSTACKED_COMMENT_GROUP
              )

              this.props.height.current = newHeight
            }
          }
        })
      })

      this.props.resizeObserver.current.observe(this.props.div.current)
    }
  }

  componentWillUnmount() {
    if (this.props.resizeObserver.current) {
      this.props.resizeObserver.current.disconnect()
    }
    this.props.onCommentGroupUnmount(this.props.annotation.id)
  }

  componentDidUpdate(_prevProps, _prevState) {
    if (!window.ResizeObserver) {
      const clientHeightChanged =
        this.props.div.current.clientHeight !== this.props.clientHeight

      if (clientHeightChanged) {
        this.props.setClientHeight(this.props.div.current.clientHeight)
        this.props.updateClientHeight()
      }
    }
  }

  render() {
    return <></>
  }
}

const CommentGroup_memoized = React.memo(CommentGroup_, areEqual)

export default function CommentGroup(props) {
  const {
    annotation,
    annotationId,
    commentReplies,
    iconColor,
    onCommentEscape,
    isAnnotatable,
    onHoverAnnotation,
    isSelected,
    managedTop,
    onDeleteAnnotation,
    onDeletePopupCancel,
    onDeletePopupConfirm,
    sessionUserObject,
    shouldCommentGroupStack
  } = props
  const div = useRef(null)
  const ariaNotify = useRef(null)
  const resizeObserver = useRef(null)
  const height = useRef(null)
  const [isReplying, setIsReplying] = useState(props.isCommenting)
  const [clientHeight, setClientHeight] = useState(0)
  const [
    preventCommentGroupTopAnimation,
    setPreventCommentGroupTopAnimation
  ] = useState(true)
  const [isCommentHandlingDelete, setIsCommentHandlingDelete] = useState(false)

  const updateClientHeight = () => {
    if (window.ResizeObserver) {
      return
    }

    if (div.current) {
      const isStacked = isCommentGroupStacked(
        props.isSelected,
        props.shouldCommentGroupStack
      )

      props.onCommentGroupHeightChange(
        props.annotation.id,
        div.current.clientHeight,
        isStacked ? STACKED_COMMENT_GROUP : UNSTACKED_COMMENT_GROUP
      )
    }
  }

  const handleCommentGroupBlur = event => {
    props.onCommentGroupBlur(event)
  }

  const handleReplySave = ({ contents, annotationId }) => {
    props
      .onCommentSave({
        annotationPayload: { contents },
        inreplyto: props.annotationId,
        annotationId
      })
      .then(() => {
        ariaNotify.current.textContent = formatMessage(
          'Comment saved successfully'
        )
        // We wait a moment for the screen reader to register the
        // new text, and then we clear it out so that if another
        // comment is saved we can announce it again.
        setTimeout(() => {
          ariaNotify.current.textContent = ''
        }, 500)
      })
      .catch(error => {
        console.error(error)
        props.onSelectComment(false)
      })
    setIsReplying(false)
  }

  const handleCommentHandlingDeleteChange = isCommentHandlingDelete => {
    setIsCommentHandlingDelete(isCommentHandlingDelete)
  }

  useEffect(() => {
    const wasDeselected = props.isSelected === false
    if (wasDeselected) {
      setIsReplying(false)
    }
  }, [props.isSelected])

  useEvent('blur', handleCommentGroupBlur, div.current, {
    capture: true
  })

  const sortedReplies = commentReplies.sort(byCreatedAt)
  const replies = sortedReplies.map((comment, index) => {
    const isFirstComment =
      !annotation.deleted && commentReplies[0].id === comment.id

    const isStacked = isCommentGroupStacked(isSelected, shouldCommentGroupStack)

    const isLastVisibleComment =
      isReplying === false &&
      (sortedReplies.length - 1 === index || (isStacked && index === 0))

    const canEdit =
      isAnnotatable &&
      sessionUserObject.user_id === comment.user_id &&
      !comment.deleted

    return (
      <Comment
        annotationId={comment.id}
        annotationType={annotation.type}
        canEdit={canEdit}
        canDelete={
          isAnnotatable &&
          (sessionUserObject.permissions.includes('manage') ||
            sessionUserObject.user_id === comment.user_id)
        }
        deletedAt={comment.deleted_at}
        deletedBy={comment.deleted_by}
        contents={comment.contents}
        iconColor={iconColor}
        isCommentGroupStacked={isStacked}
        isDeleted={comment.deleted}
        isFirst={isFirstComment}
        isLastVisibleComment={isLastVisibleComment}
        isSelected={isSelected}
        key={comment.id}
        numberOfReplies={sortedReplies.length - 1}
        onAcknowledgeDeletion={onDeleteAnnotation}
        onCommentHandlingDelete={handleCommentHandlingDeleteChange}
        onCommentSave={handleReplySave}
        onDeleteAnnotation={onDeleteAnnotation}
        onDeletePopupCancel={onDeletePopupCancel}
        onDeletePopupConfirm={onDeletePopupConfirm}
        placeholder={generatePlaceholder(
          isFirstComment,
          sessionUserObject.anonymous
        )}
        replyFormVisible={
          isSelected && !isReplying && isAnnotatable && !annotation.deleted
        }
        isRootComment={isFirstComment}
        shouldShowContent={true}
        user_name={comment.user_name}
      />
    )
  })

  const showReplyButton =
    replies.length > 0 &&
    isSelected &&
    !isReplying &&
    isAnnotatable &&
    !annotation.deleted

  const top_ = managedTop != null ? managedTop : props.top

  const handleCancelReply = () => {
    setIsReplying(false)
    props.onDeleteAnnotation()
  }

  return (
    <>
      <Spring
        to={{ top: `${top_}px` }}
        config={{
          duration: 150
        }}
      >
        {styles => (
          <ul
            className={classnames('CommentGroup', {
              'CommentGroup--active': isSelected
            })}
            data-annotation-id={annotationId}
            id={`CommentGroup-${annotationId}`}
            ref={div}
            tabIndex={-1}
            style={preventCommentGroupTopAnimation ? { top: top_ } : styles}
            onMouseEnter={() => onHoverAnnotation(annotationId)}
            onMouseLeave={() => onHoverAnnotation(null)}
          >
            {annotation.deleted && (
              <Comment
                annotationId={uuid.v4()}
                annotationType={annotation.type}
                canEdit={false}
                deletedAt={annotation.deleted_at}
                deletedBy={annotation.deleted_by}
                contents={''}
                iconColor={iconColor}
                isDeleted={true}
                isFirst={true}
                isLastVisibleComment={true}
                isReplyForm={false}
                isRootComment={true}
                isSelected={isSelected}
                key={'deleted'}
                onAcknowledgeDeletion={() => onDeleteAnnotation(annotation.id)}
                onCommentHandlingDelete={handleCommentHandlingDeleteChange}
                onCommentSave={null}
                onDeleteAnnotation={null}
                onDeletePopupCancel={onDeletePopupCancel}
                onDeletePopupConfirm={onDeletePopupConfirm}
                placeholder={''}
                replyFormVisible={false}
                shouldShowContent={false}
                user_name={annotation.user_name}
              />
            )}

            {isSelected ||
            shouldCommentGroupStack === false ||
            isCommentHandlingDelete
              ? replies
              : replies.slice(0, 1)}

            {isSelected && isReplying && (
              <Comment
                annotationId={uuid.v4()}
                annotationType={annotation.type}
                canDelete={isAnnotatable}
                canEdit={isAnnotatable}
                contents={''}
                iconColor={iconColor}
                isDeleted={false}
                deletedAt={annotation.deleted_at}
                deletedBy={annotation.deleted_by}
                isFirst={commentReplies.length === 0}
                isLastVisibleComment={false}
                isReplyForm={true}
                isRootComment={false}
                isSelected={isSelected}
                key={'new'}
                onAcknowledgeDeletion={null}
                onCommentEscape={onCommentEscape}
                onCommentHandlingDelete={handleCommentHandlingDeleteChange}
                onCommentSave={handleReplySave}
                onDeleteAnnotation={handleCancelReply}
                onDeletePopupCancel={onDeletePopupCancel}
                onDeletePopupConfirm={onDeletePopupConfirm}
                placeholder={generatePlaceholder(
                  replies.length <= 0,
                  sessionUserObject.anonymous
                )}
                replyFormVisible={true}
                shouldShowContent={true}
                user_name={sessionUserObject.user_name}
              />
            )}

            {isSelected === false && shouldCommentGroupStack === true && (
              <React.Fragment>
                {replies.length > 1 && <div className="Comment2" />}
                {replies.length > 2 && <div className="Comment3" />}
              </React.Fragment>
            )}

            <li className="CommentReplyForm">
              <div
                className={`CommentReplyForm-inner ${
                  showReplyButton ? 'visible' : 'hidden'
                }`}
              >
                <button
                  className="Comment-reply-button inst-button"
                  aria-label={formatMessage('Reply')}
                  onClick={() => setIsReplying(true)}
                >
                  {formatMessage('Reply')}
                </button>
              </div>
            </li>
            <li
              className="CommentGroup-aria-notify"
              aria-live="polite"
              ref={ariaNotify}
            />
          </ul>
        )}
      </Spring>
      <CommentGroup_memoized
        {...props}
        div={div}
        ariaNotify={ariaNotify}
        isReplying={isReplying}
        setIsReplying={setIsReplying}
        clientHeight={clientHeight}
        setClientHeight={setClientHeight}
        preventCommentGroupTopAnimation={preventCommentGroupTopAnimation}
        setPreventCommentGroupTopAnimation={setPreventCommentGroupTopAnimation}
        isCommentHandlingDelete={isCommentHandlingDelete}
        setIsCommentHandlingDelete={setIsCommentHandlingDelete}
        updateClientHeight={updateClientHeight}
        resizeObserver={resizeObserver}
        handleCommentGroupBlur={handleCommentGroupBlur}
        handleReplySave={handleReplySave}
        handleCommentHandlingDeleteChange={handleCommentHandlingDeleteChange}
        height={height}
      />
    </>
  )
}
